import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Users, AlertTriangle, Clock, CalendarX, FileText, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const formatarMinutos = (min) => {
  if (!min && min !== 0) return '00:00';
  const abs = Math.abs(min);
  const h = Math.floor(abs / 60);
  const m = Math.round(abs % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const formatarHoras = (h) => {
  if (!h && h !== 0) return '00:00';
  const abs = Math.abs(h);
  const horas = Math.floor(abs);
  const min = Math.round((abs % 1) * 60);
  return `${String(horas).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
};

const AbsenteismoPanel = ({ empresas, empresaFiltro }) => {
  const { user } = useAuth();
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataInicio, setDataInicio] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
  });
  const [dataFim, setDataFim] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });

  const carregar = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = { data_inicio: dataInicio, data_fim: dataFim };
      if (empresaFiltro) params.empresa_id = empresaFiltro;
      const response = await axios.get(`${API}/api/banco-horas/absenteismo`, {
        params,
        headers: { Authorization: `Bearer ${token}` }
      });
      setDados(response.data);
    } catch (err) {
      console.error('Erro ao carregar absenteísmo:', err);
    } finally {
      setLoading(false);
    }
  };

  const exportarCSV = () => {
    if (!dados?.por_funcionario?.length) return;
    const BOM = '\uFEFF';
    const linhas = [['Funcionário', 'Faltas', 'Atestados', 'Horas Declaradas', 'Atrasos', 'Saídas Antec.', 'Taxa', 'Severidade']];
    for (const f of dados.por_funcionario) {
      linhas.push([
        f.funcionario_nome,
        String(f.total_faltas || 0),
        String(f.total_atestados || 0),
        formatarHoras(f.total_horas_declaradas || 0),
        formatarMinutos(f.total_atrasos_minutos),
        formatarMinutos(f.total_saidas_antecipadas_minutos),
        `${((f.taxa || 0)).toFixed(1)}%`,
        (f.taxa || 0) > 10 ? 'Alta' : (f.taxa || 0) > 5 ? 'Média' : 'Baixa'
      ]);
    }
    const csv = BOM + linhas.map(l => l.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', `absenteismo_${dataInicio}_${dataFim}.csv`);
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    carregar();
  }, [empresaFiltro]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Data Início</label>
          <input
            type="date"
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Data Fim</label>
          <input
            type="date"
            value={dataFim}
            onChange={(e) => setDataFim(e.target.value)}
            className="rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:outline-none text-sm"
          />
        </div>
        <Button onClick={carregar} disabled={loading}>
          {loading ? 'Carregando...' : 'Atualizar'}
        </Button>
        {dados?.por_funcionario?.length > 0 && (
          <Button onClick={exportarCSV} variant="outline">
            <Download className="h-4 w-4 mr-1" /> Exportar CSV
          </Button>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="loading-spinner w-8 h-8 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
        </div>
      )}

      {!loading && !dados && (
        <Card>
          <CardContent className="py-8 text-center text-gray-500">
            <Users className="mx-auto h-12 w-12 text-gray-300 mb-2" />
            <p>Nenhum dado de absenteísmo disponível para o período.</p>
          </CardContent>
        </Card>
      )}

      {!loading && dados && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Taxa Geral</p>
                    <p className={`text-2xl font-bold ${(dados.taxa_geral || 0) > 10 ? 'text-red-600' : (dados.taxa_geral || 0) > 5 ? 'text-amber-600' : 'text-green-600'}`}>
                      {(dados.taxa_geral || 0).toFixed(1)}%
                    </p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-gray-400" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Faltas</p>
                    <p className="text-2xl font-bold text-red-600">{dados.total_faltas || 0}</p>
                  </div>
                  <CalendarX className="h-8 w-8 text-red-400" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Atestados</p>
                    <p className="text-2xl font-bold text-purple-600">{dados.total_atestados || 0}</p>
                  </div>
                  <FileText className="h-8 w-8 text-purple-400" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Horas Declaradas</p>
                    <p className="text-2xl font-bold text-blue-600">{formatarHoras(dados.total_horas_declaradas)}</p>
                  </div>
                  <FileText className="h-8 w-8 text-blue-400" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Atrasos</p>
                    <p className="text-2xl font-bold text-amber-600">{formatarMinutos(dados.total_atrasos_minutos)}</p>
                  </div>
                  <Clock className="h-8 w-8 text-amber-400" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Saídas Antecipadas</p>
                    <p className="text-2xl font-bold text-orange-600">{formatarMinutos(dados.total_saidas_antecipadas_minutos)}</p>
                  </div>
                  <Clock className="h-8 w-8 text-orange-400" />
                </div>
              </CardContent>
            </Card>
          </div>

          {dados.por_funcionario && dados.por_funcionario.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Absenteísmo por Funcionário</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-gray-500">
                        <th className="pb-2 font-medium">Funcionário</th>
                        <th className="pb-2 font-medium text-right">Faltas</th>
                        <th className="pb-2 font-medium text-right">Atestados</th>
                        <th className="pb-2 font-medium text-right">Horas Decl.</th>
                        <th className="pb-2 font-medium text-right">Atrasos</th>
                        <th className="pb-2 font-medium text-right">Saídas Antec.</th>
                        <th className="pb-2 font-medium text-right">Taxa</th>
                        <th className="pb-2 font-medium text-right">Severidade</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dados.por_funcionario.map((f, idx) => (
                        <tr key={f.funcionario_id || idx} className="border-b last:border-0">
                          <td className="py-2">{f.funcionario_nome}</td>
                          <td className="py-2 text-right">{f.total_faltas || 0}</td>
                          <td className="py-2 text-right text-purple-600">{f.total_atestados || 0}</td>
                          <td className="py-2 text-right text-blue-600">{formatarHoras(f.total_horas_declaradas || 0)}</td>
                          <td className="py-2 text-right">{formatarMinutos(f.total_atrasos_minutos)}</td>
                          <td className="py-2 text-right">{formatarMinutos(f.total_saidas_antecipadas_minutos)}</td>
                          <td className="py-2 text-right font-medium">{((f.taxa || 0)).toFixed(1)}%</td>
                          <td className="py-2 text-right">
                            <Badge variant={
                              (f.taxa || 0) > 10 ? 'destructive' :
                              (f.taxa || 0) > 5 ? 'outline' :
                              'secondary'
                            } className={
                              (f.taxa || 0) > 10 ? 'bg-red-100 text-red-700' :
                              (f.taxa || 0) > 5 ? 'bg-amber-100 text-amber-700' :
                              'bg-green-100 text-green-700'
                            }>
                              {(f.taxa || 0) > 10 ? 'Alta' : (f.taxa || 0) > 5 ? 'Média' : 'Baixa'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default AbsenteismoPanel;
